import type * as React from 'react';
import { post } from '@Domain/Api';
import userAuthenticated, {
	type AuthenticatedUserPayload,
	type UserAuthenticatedAction,
} from '@Application/Security/Authentications/Actions/userAuthenticated';
import secondFactorEnrollmentRequired, {
	type SecondFactorEnrollmentRequiredAction,
} from '@Application/Security/Authentications/Actions/secondFactorEnrollmentRequired';
import invitationAccepted, {
	type InvitationAcceptedAction,
} from '@Application/Security/Invitations/Actions/invitationAccepted';
import invitationAcceptanceFailed, {
	type InvitationAcceptanceFailedAction,
} from '@Application/Security/Invitations/Actions/invitationAcceptanceFailed';
import type AcceptInvitation from '@Application/Security/Invitations/Commands/AcceptInvitation';
import FALLBACK_MESSAGES from '@Application/Security/TokenJourney/FALLBACK_MESSAGES';
import resolveFailureReason from '@Application/Security/TokenJourney/resolveFailureReason';
import extractMessages from '@Infra/FetchLib/ApiError/extractMessages';
import parameters from '../../../parameters';

/**
 * Mirrors the `LoginResponse` union declared in `Application/Authentications/login.ts`, which does
 * not export it. `POST /api/invitations/{token}` answers with that same shape: duplicating the type
 * alias here is the only option without touching `Authentications/**`, but the status handling below
 * is a straight copy of `login.ts`'s switch, not a second design.
 */
type LoginResponse =
	| { status: 'authenticated'; user: AuthenticatedUserPayload }
	| { status: 'mfa_required' }
	| { status: 'mfa_enrollment_required' };

type Actions =
	| InvitationAcceptedAction
	| SecondFactorEnrollmentRequiredAction
	| UserAuthenticatedAction
	| InvitationAcceptanceFailedAction;

export default (command: AcceptInvitation) => {
	return async (dispatch: React.Dispatch<Actions>): Promise<Actions> => {
		try {
			// Public, token-carried route: there is no session to refresh on a 401.
			const response = await post<LoginResponse>(
				`${parameters.api.host}/api/invitations/${command.token}`,
				command.toJSON(),
				{ retryOnUnauthorized: false },
			);

			const acceptedAction = invitationAccepted();
			dispatch(acceptedAction);

			switch (response.status) {
				case 'mfa_enrollment_required': {
					// Own the same store-mutating gesture `login.ts` performs on this branch: `RequireChallenge`
					// reads `sessionStatus`, which this action alone moves toward `mfa_enrollment_required`.
					const action = secondFactorEnrollmentRequired();
					dispatch(action);
					return action;
				}
				case 'authenticated': {
					// Own the same store-mutating gesture `login.ts` performs on this branch: `RequireAnonymous`
					// reads `sessionStatus`, which this action alone moves toward `authenticated`.
					const action = userAuthenticated(response.user);
					dispatch(action);
					return action;
				}
				case 'mfa_required': {
					return acceptedAction;
				}
				default: {
					// Exhaustive guard: a fourth `status` value fails compilation here rather than silently falling through.
					const exhaustiveCheck: never = response;
					throw new Error(
						`Unhandled invitation acceptance status: ${JSON.stringify(exhaustiveCheck)}`,
					);
				}
			}
		} catch (error) {
			const extractedMessages = extractMessages(error);
			const reason = resolveFailureReason(error);
			const message = extractedMessages?.message ?? FALLBACK_MESSAGES.invitationGone;
			const action = invitationAcceptanceFailed(extractedMessages, reason, message);
			dispatch(action);
			return Promise.reject(action);
		}
	};
};
